/**
 * Canonical location rules (legacy `district` / `province` + multi `areas`).
 * KEEP IN SYNC: HumQadam-mobile-frontend/App/helper/locationHelpers.js
 */

/** Normalize an id field (ObjectId, populated { _id }, or string). */
export function normalizeLocationId(v) {
  if (v == null || v === '') return null;
  if (typeof v === 'object' && v._id != null) return String(v._id);
  return String(v).trim();
}

/**
 * Organization serves the user's province/district (areas or legacy fields).
 * Same rules as backend buildOrgLocationConditions / web API org queries.
 */
export function orgServesLocation(org, provinceId, districtId) {
  const pId = provinceId ? normalizeLocationId(provinceId) : null;
  const dId = districtId ? normalizeLocationId(districtId) : null;
  if (!pId) return false;

  if (org.areas && org.areas.length > 0) {
    return org.areas.some((area) => {
      const areaProvinceId = normalizeLocationId(area.province);
      if (areaProvinceId !== pId) return false;
      const areaDistricts = area.districts || [];
      if (areaDistricts.length === 0) return true;
      return areaDistricts.some((d) => normalizeLocationId(d) === dId);
    });
  }

  const orgProvinceId = normalizeLocationId(org.province);
  if (orgProvinceId !== pId) return false;
  if (!dId) return true;
  const orgDistrictId = normalizeLocationId(org.district);
  return !orgDistrictId || orgDistrictId === dId;
}

/**
 * Guidance step applies to the selected district (legacy step.district or areas).
 * Province-wide area rows require the user's province to match the area's province.
 * If districtId is null/empty, returns true (no district filter).
 */
export function guidanceStepServesDistrict(step, districtId, provinceId) {
  const districtStr = districtId != null ? normalizeLocationId(districtId) : '';
  if (!districtStr) return true;

  const legacyDist = normalizeLocationId(step?.district);
  if (legacyDist && legacyDist === districtStr) return true;

  const provinceStr = provinceId != null ? normalizeLocationId(provinceId) : '';

  const areas = step.areas || [];
  for (let i = 0; i < areas.length; i += 1) {
    const area = areas[i];
    const areaProvNorm = normalizeLocationId(area.province) || '';
    const dlist = (area.districts || [])
      .map((d) => normalizeLocationId(d))
      .filter(Boolean);
    if (dlist.includes(districtStr)) return true;
    if (
      dlist.length === 0 &&
      provinceStr &&
      areaProvNorm &&
      areaProvNorm === provinceStr
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Human-readable location line: legacy district + areas (admin parity, user language via getLabel).
 * @param {object} entity - { district?, areas?[] }
 * @param {Array} districts - list with _id, name (translations array)
 * @param {Array} provinces - list with _id, name (translations array)
 * @param {(translations: Array) => string} getLabel - e.g. (t) => getTranslation(t, langId)
 * @returns {string|null}
 */
export function formatEntityDistrictAndAreasLabel(
  entity,
  districts,
  provinces,
  getLabel,
) {
  if (!entity || typeof getLabel !== 'function') return null;
  const parts = [];

  if (entity.district) {
    const dist = districts?.find(
      (d) => normalizeLocationId(d._id) === normalizeLocationId(entity.district),
    );
    const label = dist ? getLabel(dist.name) : '';
    if (label) parts.push(label);
  }

  if (entity.areas?.length) {
    const chunks = [];
    entity.areas.forEach((area) => {
      const prov = provinces?.find(
        (p) => normalizeLocationId(p._id) === normalizeLocationId(area.province),
      );
      const provName = prov ? getLabel(prov.name) : '?';
      const dIds = area.districts || [];
      if (dIds.length === 0) {
        chunks.push(`${provName} (all districts)`);
      } else {
        const names = dIds
          .map((did) => {
            const d = districts?.find(
              (x) => normalizeLocationId(x._id) === normalizeLocationId(did),
            );
            return d ? getLabel(d.name) : null;
          })
          .filter(Boolean);
        chunks.push(names.length ? `${provName}: ${names.join(', ')}` : provName);
      }
    });
    const areaStr = chunks.join('; ');
    if (areaStr) parts.push(areaStr);
  }

  if (parts.length === 0) return null;
  return parts.join(' � ');
}
